
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDatabase, ref, onValue, off, get, update, remove } from 'firebase/database';
import MainLayout from '../components/MainLayout';
import './TraitsAssessment.css';

const TraitsAssessment = ({ students: searchResults, searchPerformed }) => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [students, setStudents] = useState([]);
  const [traits, setTraits] = useState({});
  const [scores, setScores] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const db = getDatabase();

  const sessions = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return `${year}/${year + 1}`;
  });
  const terms = ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'];

  const psychomotorSkills = ['Handwriting','Verbal Fluency','Games and sports','Gymnastics','Music','Construction'];
  const affectiveAreas = ['Attentiveness','Punctuality','Neatness','Politeness','Relationship with others','Honesty'];
  const ratingLevels = ['Excellent', 'Good', 'Fair', 'Poor', 'Very poor'];

  useEffect(() => {
    if (searchPerformed) {
        setStudents(searchResults || []);
         if (searchResults && searchResults.length > 0) {
            const firstStudentClass = searchResults[0].className || searchResults[0].class;
            if (firstStudentClass && searchResults.every(s => (s.className || s.class) === firstStudentClass)) {
                setSelectedClass(firstStudentClass);
            }
        } else {
            setTraits({});
        }
    }
}, [searchResults, searchPerformed]);


  useEffect(() => {
    const appSettingsRef = ref(db, 'appSettings/currentSession');
    onValue(appSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const { session, term } = snapshot.val();
        setSelectedSession(session);
        setSelectedTerm(term);
      }
    });
    return () => off(appSettingsRef);
  }, [db]);

  useEffect(() => {
    if (currentUser) {
      const userRef = ref(db, `users/${currentUser.uid}`);
      const callback = (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setUserData(data);
          if (data.role?.toLowerCase() === 'form-master' && !searchPerformed) {
            setSelectedClass(data.assignedClass || '');
          }
        }
      };
      onValue(userRef, callback);
      return () => off(userRef, 'value', callback);
    }
  }, [currentUser, db, searchPerformed]);

  useEffect(() => {
    const role = userData?.role?.toLowerCase();
    if (['admin', 'principal', 'form-master'].includes(role) || searchPerformed) {
      const classesRef = ref(db, 'classes');
      const callback = (snapshot) => {
        setAllClasses(snapshot.exists() ? Object.keys(snapshot.val()).sort() : []);
      };
      onValue(classesRef, callback);
      return () => off(classesRef, 'value', callback);
    }
  }, [userData, db, searchPerformed]);

  const handleSearch = useCallback(async () => {
    if (!selectedClass || !selectedSession || !selectedTerm) return alert('Please select class, session, and term.');
    setLoading(true);

    if (searchPerformed) {
        // If a search was performed from the parent, just load scores for those students.
        setLoading(true);
        const studentIds = students.map(s => s.id);
        try {
            const sessionPath = encodeURIComponent(selectedSession);
            const termPath = encodeURIComponent(selectedTerm.toUpperCase());
            const traitsRef = ref(db, `traits/${sessionPath}/${termPath}/${selectedClass}`);
            const traitsSnap = await get(traitsRef);
            const allTraits = traitsSnap.val() || {};
            const studentTraits = {};
            studentIds.forEach(id => {
                if (allTraits[id]) {
                    studentTraits[id] = allTraits[id];
                }
            });
            setTraits(studentTraits);

            const scoresRef = ref(db, `ca_scores/${sessionPath}/${termPath}/${selectedClass}`);
            const scoresSnap = await get(scoresRef);
            setScores(scoresSnap.exists() ? scoresSnap.val() : {});

            const pubRef = ref(db, `published/${sessionPath}/${termPath}/${selectedClass}`);
            const pubSnap = await get(pubRef);
            setIsPublished(pubSnap.exists() ? pubSnap.val() : false);

        } catch (err) {
            console.error("Failed to fetch data:", err);
            alert('Failed to fetch data for searched students.');
        } finally {
            setLoading(false);
        }
        return;
    }

    try {
      const studentSnapshot = await get(ref(db, 'users'));
      const fetchedStudents = [];
      if (studentSnapshot.exists()) {
        studentSnapshot.forEach((childSnap) => {
          const student = childSnap.val();
          const studentClass = (student.className || student.class || student.studentClass || '').trim().toLowerCase();
          const selectedClassLower = selectedClass.trim().toLowerCase();
          if (student.role?.toLowerCase() === 'student' && studentClass.replace(/\s+/g, '') === selectedClassLower.replace(/\s+/g, '')) {
            fetchedStudents.push({ id: childSnap.key, ...student, profilePictureUrl: student.profilePictureUrl || student.imageUrl });
          }
        });
      }
      setStudents(fetchedStudents);

      const sessionPath = encodeURIComponent(selectedSession);
      const termPath = encodeURIComponent(selectedTerm.toUpperCase());

      const traitsRef = ref(db, `traits/${sessionPath}/${termPath}/${selectedClass}`);
      const traitsSnap = await get(traitsRef);
      setTraits(traitsSnap.exists() ? traitsSnap.val() : {});

      const scoresRef = ref(db, `ca_scores/${sessionPath}/${termPath}/${selectedClass}`);
      const scoresSnap = await get(scoresRef);
      setScores(scoresSnap.exists() ? scoresSnap.val() : {});

      const pubRef = ref(db, `published/${sessionPath}/${termPath}/${selectedClass}`);
      const pubSnap = await get(pubRef);
      setIsPublished(pubSnap.exists() ? pubSnap.val() : false);

      if(fetchedStudents.length === 0) alert('No students found for the selected class.');

    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to fetch data. Please check your connection');
    } finally {
      setLoading(false);
    }
  }, [db, selectedClass, selectedSession, selectedTerm, searchPerformed, students]);

  const handleTraitChange = (studentId, type, key, value) => {
    setTraits((prev) => ({ ...prev, [studentId]: { ...prev[studentId], [type]: { ...prev[studentId]?.[type], [key]: value } } }));
  };
  const handleRemarkChange = (studentId, remarkType, value) => {
    setTraits((prev) => ({ ...prev, [studentId]: { ...prev[studentId], [remarkType]: value } }));
  };
  const handleAttendanceChange = (studentId, field, value) => {
    const numValue = Math.max(0, Number(value));
    setTraits((prev) => ({ ...prev, [studentId]: { ...prev[studentId], [field]: numValue } }));
  };

  const handleSaveAll = async () => {
    if (isPublished) return alert('Cannot save; results are published.');
    if (students.length === 0) return alert('No student data to save.');

    setIsSaving(true);
    try {
      const sessionPath = encodeURIComponent(selectedSession);
      const termPath = encodeURIComponent(selectedTerm.toUpperCase());
      const updates = {};
      students.forEach(student => {
        if (traits[student.id]) {
          updates[`traits/${sessionPath}/${termPath}/${selectedClass}/${student.id}`] = traits[student.id];
        }
      });
      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
        alert('All changes have been saved successfully.');
      } else {
        alert('No changes to save.');
      }
    } catch (error) {
      console.error('Error saving traits:', error);
      alert('An error occurred while saving traits.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveStudent = async (studentId) => {
    if (isPublished) return alert('Cannot save; results are published.');
    if (!traits[studentId]) return alert('No data to save for this student.');

    try {
      const sessionPath = encodeURIComponent(selectedSession);
      const termPath = encodeURIComponent(selectedTerm.toUpperCase());
      await update(ref(db, `traits/${sessionPath}/${termPath}/${selectedClass}/${studentId}`), traits[studentId]);
      alert('Student traits saved successfully.');
    } catch (error) {
      console.error('Error saving student traits:', error);
      alert('Error saving student traits.');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (isPublished) return alert('Cannot delete; results are published.');
    if (!window.confirm('Are you sure you want to delete this student\'s traits?')) return;

    try {
      const sessionPath = encodeURIComponent(selectedSession);
      const termPath = encodeURIComponent(selectedTerm.toUpperCase());
      await remove(ref(db, `traits/${sessionPath}/${termPath}/${selectedClass}/${studentId}`));
      setTraits(prev => {
        const newTraits = { ...prev };
        delete newTraits[studentId];
        return newTraits;
      });
      alert('Student traits deleted successfully.');
    } catch (error) {
      console.error('Error deleting student traits:', error);
      alert('Error deleting student traits.');
    }
  };

  const role = userData?.role?.toLowerCase();
  if (role && !['admin', 'principal', 'form-master', 'teacher'].includes(role)) {
    return <MainLayout><div className="alert alert-danger">Access Denied</div></MainLayout>;
  }

  const canSelectClass = role === 'admin' || role === 'principal' || searchPerformed;


  const getSubjectTotal = (studentId, subject) => {
    const subjectScores = scores[subject]?.[studentId] || {};
    return Object.values(subjectScores).reduce((acc, score) => acc + (Number(score) || 0), 0);
  };

  return (
    <MainLayout>
      <div className="traits-assessment-container">
        <h4>Traits and Affective Domain Assessment</h4>
        <div className="filters-container row mb-3">
          <div className="col-md-3"><label>Session</label><select className="form-control" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>{sessions.map((s) => (<option key={s}>{s}</option>))}</select></div>
          <div className="col-md-3"><label>Term</label><select className="form-control" value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>{terms.map((t) => (<option key={t}>{t}</option>))}</select></div>
          <div className="col-md-3"><label>Class</label><select className="form-control" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} disabled={!canSelectClass}>{canSelectClass ? (<><option value="">-- Select Class --</option>{allClasses.map((c) => (<option key={c} value={c}>{c}</option>))}</>) : (<option value={selectedClass}>{selectedClass}</option>)}</select></div>
          <div className="col-md-3 align-self-end"><button className="btn btn-primary w-100" onClick={handleSearch} disabled={loading}>{loading ? 'Searching...' : (searchPerformed ? 'Load Data' : 'Search')}</button></div>
        </div>
        {isPublished && <div className="alert alert-warning">Results are published and cannot be modified.</div>}
        {students.length > 0 && (<div className="card-footer text-end mb-3"><button className="btn btn-success" onClick={handleSaveAll} disabled={isPublished || isSaving || loading}>{isSaving ? 'Saving...' : 'Save All Changes'}</button></div>)}
        {!loading && students.map((student) => (
            <div key={student.id} className="card mb-3">
              <div className="card-header fw-bold" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><div style={{ display: 'flex', alignItems: 'center' }}><img src={student.profilePictureUrl || '/default-avatar.png'} alt={student.fullName} style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px', border: '1px solid #ccc' }}/>{student.fullName}</div><div><button className="btn btn-primary btn-sm me-2" onClick={() => handleSaveStudent(student.id)} disabled={isPublished}>Save</button><button className="btn btn-danger btn-sm" onClick={() => handleDeleteStudent(student.id)} disabled={isPublished}>Delete</button></div></div>
              <div className="card-body">
                <div className="row"><div className="col-md-6"><div><label>Regno: </label>{student.studentId || student.regNo}</div><div><label>Class: </label>{student.className || student.class}</div></div><div className="col-md-6"><table className="table-tiny table-striped small"><thead><tr><th>Subject</th><th>Total</th></tr></thead><tbody>{Object.keys(scores || {}).map(subject => (<tr key={subject}><td>{subject}</td><td>{getSubjectTotal(student.id, subject)}</td></tr>))}</tbody></table></div></div>
                <div className="row mt-3"><div className="col-md-6"><table className="traits-table-update table-bordered"><tbody><tr><td colSpan="2"><label>Psychomotor Skills</label></td></tr>{psychomotorSkills.map(skill => (<tr key={skill}><td>{skill}</td><td>{ratingLevels.map(level => (<label key={level}><input type="radio" name={`psychomotor-${skill}-${student.id}`} value={level} checked={traits[student.id]?.psychomotor?.[skill] === level} onChange={(e) => handleTraitChange(student.id, 'psychomotor', skill, e.target.value)} disabled={isPublished} /> {level}</label>))}</td></tr>))}</tbody></table></div><div className="col-md-6"><table className="traits-table-update table-bordered"><tbody><tr><td colSpan="2"><label>Affective Areas</label></td></tr>{affectiveAreas.map(area => (<tr key={area}><td>{area}</td><td>{ratingLevels.map(level => (<label key={level}><input type="radio" name={`affective-${area}-${student.id}`} value={level} checked={traits[student.id]?.affective?.[area] === level} onChange={(e) => handleTraitChange(student.id, 'affective', area, e.target.value)} disabled={isPublished} /> {level}</label>))}</td></tr>))}</tbody></table></div></div>
                <div className="row mt-3"><div className="col-md-6"><label>Days School Opened</label><input type="number" className="form-control" value={traits[student.id]?.daysOpened || ''} onChange={(e) => handleAttendanceChange(student.id, 'daysOpened', e.target.value)} disabled={isPublished} /></div><div className="col-md-6"><label>Days Present</label><input type="number" className="form-control" value={traits[student.id]?.daysPresent || ''} onChange={(e) => handleAttendanceChange(student.id, 'daysPresent', e.target.value)} disabled={isPublished} /></div></div>
                <div className="row mt-3"><div className="col-md-6"><label>Class teacher's remark</label><input type="text" className="form-control" value={traits[student.id]?.teacherRemark || ''} onChange={(e) => handleRemarkChange(student.id, 'teacherRemark', e.target.value)} disabled={isPublished} /></div>{role === 'principal' && <div className="col-md-6"><label>Principal's remark</label><input type="text" className="form-control" value={traits[student.id]?.principalRemark || ''} onChange={(e) => handleRemarkChange(student.id, 'principalRemark', e.target.value)} disabled={isPublished} /></div>}</div>
              </div>
            </div>
        ))}
        {!loading && searchPerformed && students.length === 0 && (
            <div className="alert alert-info">Please search for a student to begin, or use the filters above and click 'Load Data'.</div>
        )}
      </div>
    </MainLayout>
  );
};

export default TraitsAssessment;
